import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  OAuthProvider,
  signOut,
  onAuthStateChanged 
} from 'firebase/auth';
import { auth } from '../services/firebase';
import { trackUserLogin, trackUserLogout, setAnalyticsUserId, clearAnalyticsUserId } from '../services/analytics';
import { getUserAccess } from '../services/accessControlService';

/**
 * Get the current user's ID token
 * @returns {Promise<string|null>} ID token or null if not authenticated
 */
export const getIdToken = async () => {
  if (!auth.currentUser) {
    return null;
  }
  try {
    return await auth.currentUser.getIdToken();
  } catch (error) {
    console.error('Error getting ID token:', error);
    return null;
  }
};

const AuthContext = createContext({});

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userAccess, setUserAccess] = useState(null); // Product access map
  const [accessLoading, setAccessLoading] = useState(true); // Start true to wait for access data

  const googleProvider = new GoogleAuthProvider();
  const appleProvider = new OAuthProvider('apple.com');

  const signInWithGoogle = async () => {
    // Auth is handled by backend via ALLOWED_EMAILS whitelist
    // Try popup first, fall back to redirect if COOP blocks it
    try {
      const result = await signInWithPopup(auth, googleProvider);
      // Log successful login
      trackUserLogin('google', result.user?.email?.split('@')[1]);
      return result;
    } catch (error) {
      // If popup is blocked by COOP policy or popup blocker, use redirect
      if (error.code === 'auth/popup-blocked' || 
          error.code === 'auth/popup-closed-by-user' ||
          error.message?.includes('Cross-Origin-Opener-Policy')) {
        return await signInWithRedirect(auth, googleProvider);
      }
      throw error;
    }
  };

  const signInWithApple = async () => {
    try {
      const result = await signInWithPopup(auth, appleProvider);
      if (result?.user) {
        const email = result.user?.email;
        trackUserLogin('apple', email ? email.split('@')[1] : null);
      }
      return result;
    } catch (error) {
      if (error.code === 'auth/popup-blocked' ||
          error.code === 'auth/popup-closed-by-user' ||
          error.message?.includes('Cross-Origin-Opener-Policy')) {
        return await signInWithRedirect(auth, appleProvider);
      }
      throw error;
    }
  };

  const logout = () => {
    setUserAccess(null); // Clear access on logout
    setAccessLoading(true); // Reset to loading state so next user must wait for fresh access data
    trackUserLogout(); // Track logout event before clearing user ID
    clearAnalyticsUserId(); // Clear analytics user ID
    return signOut(auth);
  };

  // Fetch user access when user logs in
  useEffect(() => {
    const fetchUserAccess = async () => {
      // Wait for auth to finish loading before making access decisions
      if (loading) {
        return;
      }

      if (!currentUser) {
        setUserAccess(null);
        setAccessLoading(false); // No user, no access to load
        return;
      }

      try {
        setAccessLoading(true);
        const accessData = await getUserAccess();
        setUserAccess(accessData.access || {});
      } catch (error) {
        console.error('Error fetching user access:', error);
        // Set empty access map on error (user can still access public routes)
        setUserAccess({});
      } finally {
        setAccessLoading(false);
      }
    };

    fetchUserAccess();
  }, [currentUser, loading]);

  useEffect(() => {
    // Handle redirect result when user returns from Google or Apple sign-in
    getRedirectResult(auth)
      .then((result) => {
        if (result?.user) {
          const providerId = result.user?.providerData?.[0]?.providerId;
          const email = result.user?.email;
          const domain = email ? email.split('@')[1] : null;
          if (providerId === 'apple.com') {
            trackUserLogin('apple_redirect', domain);
          } else {
            trackUserLogin('google_redirect', domain);
          }
        }
      })
      .catch((error) => {
        console.error('Redirect sign-in error:', error);
      });

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      // Auth is handled by backend via ALLOWED_EMAILS whitelist
      setCurrentUser(prevUser => {
        // If user changed (including sign out/sign in), reset access loading
        // This ensures fresh access data is fetched for each user
        if (prevUser?.uid !== user?.uid) {
          setAccessLoading(true);
          setUserAccess(null);
        }
        return user;
      });
      setLoading(false);
      
      // Set or clear analytics user ID based on auth state
      if (user) {
        setAnalyticsUserId(user.uid);
      } else {
        clearAnalyticsUserId();
      }
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    loading,
    userAccess,
    accessLoading,
    signInWithGoogle,
    signInWithApple,
    logout,
    getIdToken: async () => {
      if (!currentUser) return null;
      try {
        return await currentUser.getIdToken();
      } catch (error) {
        console.error('Error getting ID token:', error);
        return null;
      }
    },
    hasAccess: (productName) => {
      if (!userAccess) return false;
      return userAccess[productName] === true;
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
